<script setup lang="ts">
  import ArrowRightIcon from '@/shared/assets/icons/ArrowRight.svg';
  import Folder from '@/shared/assets/icons/Folder.svg';
  import FolderIcon from '@/shared/assets/icons/FolderIcon.svg';
  import { IconImage, Tooltip } from '@/shared/ui';
  import { getDirectoryModuleIcon } from '@/shared/ui/module-icons';
  import { useNavigationPathBuilder } from '@/shared/lib/use/useNavigationPathBuilder';
  import { useNavigationTree } from '@/shared/lib';
  import { NavigationItem } from '@/shared/lib';
  import { watch } from 'vue';

  interface Props {
    items: NavigationItem[];
    level?: number;
    showCheckboxes?: boolean;
    showIcons?: boolean;
    maxDepth?: number;
    parentId?: string | null;
    categoryInfo?: any;
  }

  const props = withDefaults(defineProps<Props>(), {
    level: 0,
    showCheckboxes: true,
    showIcons: true,
    maxDepth: Infinity,
    parentId: null,
  });

  const emit = defineEmits<{
    'item-click': [item: NavigationItem];
    'item-toggle': [item: NavigationItem];
    'item-expand': [item: NavigationItem];
    'item-navigate': [item: NavigationItem];
  }>();

  const {
    expandedItems,
    selectedItem,
    isExpanded,
    isSelected,
    hasChildren,
    canExpand,
    handleItemClick,
    handleItemExpand,
    handleItemClickAndExpand,
    initializeItems,
  } = useNavigationTree({
    showCheckboxes: props.showCheckboxes,
    showIcons: props.showIcons,
    maxDepth: props.maxDepth,
    useGlobalState: true,
  });

  const { buildPath, shouldNavigate } = useNavigationPathBuilder();

  watch(
    () => props.items,
    (newItems) => {
      if (newItems && newItems.length > 0) {
        initializeItems(newItems);
      }
    },
    { immediate: true }
  );

  const getFolderIcon = (item: NavigationItem) => {
    return isSelected(item.id) && props.level > 0 ? FolderIcon : Folder;
  };

  const shouldRenderChildren = (item: NavigationItem) => {
    return hasChildren(item) && isExpanded(item.id);
  };

  const buildParentPath = (item: NavigationItem): string => {
    if (props.parentId) {
      return `${props.parentId}-${item.id}`;
    }
    return item.id;
  };

  const shouldNavigateToItem = (item: NavigationItem): boolean => {
    if (item.metadata?.listable !== undefined) return item.metadata.listable;
    const canNavigate = shouldNavigate(item, hasChildren(item) || false);
    return canNavigate && !hasChildren(item);
  };

  const handleItemClickWithEmit = (item: NavigationItem) => {
    handleItemClick(item);
    emit('item-click', item);
  };

  const handleItemExpandWithEmit = (item: NavigationItem) => {
    handleItemExpand(item);
    emit('item-expand', item);
  };

  const handleItemClickAndExpandWithEmit = (item: NavigationItem) => {
    handleItemClickAndExpand(item, props.level);
    const itemWithPath = {
      ...item,
      fullPath: item.code ? [item.code] : [],
      moduleName: item.name,
      moduleCode: item.code,
    };
    if (shouldNavigateToItem(item)) {
      emit('item-navigate', itemWithPath);
    }
    emit('item-click', item);
    if (hasChildren(item)) emit('item-expand', item);
  };
</script>

<template>
  <div class="tree w-full min-w-0">
    <ul
      class="list-none"
      :class="level === 0 ? 'pl-0' : 'pl-4'"
    >
      <li
        v-for="item in items"
        :key="item.id"
        class="relative box-border min-w-0"
      >
        <div
          class="tree-item flex w-full min-w-0 items-center justify-start gap-2 py-1 rounded-lg transition-colors -ml-1.5 pl-1.5 mb-1 cursor-pointer text-left"
          :class="{ active: isSelected(item.id) }"
          @click="handleItemClickAndExpandWithEmit(item)"
        >
          <div
            v-if="canExpand(item, level)"
            class="w-4 h-4 shrink-0 flex items-center justify-center"
          >
            <IconImage
              :src="ArrowRightIcon"
              alt="Expandir"
              class="transition-transform duration-200 text-nude"
              :class="{ 'rotate-90': isExpanded(item.id) }"
            />
          </div>
          <div
            v-else-if="level === 0"
            class="w-4 h-4 shrink-0"
            data-testid="tree-item-arrow-slot"
          />

          <div
            v-if="showIcons && item.icon === 'module'"
            class="w-6 h-6 shrink-0 flex items-center justify-center text-gray-07"
            data-testid="tree-item-module-icon"
          >
            <component
              :is="getDirectoryModuleIcon(item.metadata?.iconId ?? undefined)"
              class="w-6 h-6"
            />
          </div>

          <div
            v-else-if="showIcons && item.icon === 'folder'"
            class="w-6 h-6 shrink-0 flex items-center justify-center text-nude"
          >
            <IconImage
              :src="getFolderIcon(item)"
              alt="Pasta"
            />
          </div>

          <Tooltip
            class="flex-1 min-w-0"
            :content="item.name"
            placement="top"
            theme="custom-lg"
          >
            <span
              class="block truncate text-sm font-medium"
              :class="
                isSelected(item.id)
                  ? 'text-[var(--theme-secondary-ink)]'
                  : 'text-[var(--theme-text)]'
              "
              data-testid="tree-item-label"
            >
              {{ item.name }}
            </span>
          </Tooltip>
        </div>

        <ul
          v-if="shouldRenderChildren(item)"
          class="list-none pl-4"
        >
          <NavigationTree
            :items="item.children || []"
            :level="level + 1"
            :show-checkboxes="showCheckboxes"
            :show-icons="showIcons"
            :max-depth="maxDepth"
            :parent-id="buildParentPath(item)"
            :category-info="categoryInfo"
            @item-click="emit('item-click', $event)"
            @item-toggle="emit('item-toggle', $event)"
            @item-expand="emit('item-expand', $event)"
            @item-navigate="emit('item-navigate', $event)"
          />
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped>
  .tree ul ul li:before {
    position: absolute;
    top: 15px;
    left: -25px;
    width: 20px;
    height: 2px;
    margin: auto;
    content: '';
    background-color: #b9babb;
  }

  .tree ul ul li:after {
    position: absolute;
    top: -5px;
    left: -25px;
    width: 2px;
    height: calc(100% + 8px);
    content: '';
    background-color: #b9babb;
  }

  .tree ul ul li:last-child:after {
    height: 20px;
  }

  .tree-item {
    transition: background-color 0.2s ease-in-out;
  }

  .tree-item:hover {
    background-color: color-mix(in srgb, var(--theme-primary) 90%, var(--theme-secondary));
  }

  .tree-item.active {
    background-color: var(--theme-secondary-tint);
  }
</style>
